'use client';

import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export function ProductDetailPrice() {
  const { t } = useTranslations();
  const settings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(state => state.product);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const variants = useVariantsStore(state => state.product.variants);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedServices = useVariantsStore(state => state.selectedServices);

  const me = useCustomerStore(state => state.me);

  const getServicesPrice = (vat = false) => {
    let price = 0;
    selectedServices.forEach(service => {
      price += vat
        ? parseFloat(service.price_with_vat.toString())
        : parseFloat(service.price.toString());
    });
    return price;
  };

  const desiredObject = variants?.length ? selectedVariant : product;
  const priceWithoutVatFirst = settings?.product_detail?.price_without_vat_first?.enabled;
  const priceDisplay = getProductPriceDisplay(desiredObject ?? product, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst,
  });
  const hasProductDiscount = priceDisplay.showPreviousPrice;

  //   const servicesPrice = selectedServices.reduce(
  //     (acc, service) => acc + roundPrice(service.price_with_vat, currency),
  //     0,
  //   );
  const unit = settings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';
  const mainIncludesVat = !priceWithoutVatFirst;
  const mainPrice =
    priceDisplay.current == null
      ? undefined
      : priceDisplay.current + getServicesPrice(mainIncludesVat);
  const mainPriceBeforeProductDiscount =
    priceDisplay.beforeDiscount == null
      ? undefined
      : priceDisplay.beforeDiscount + getServicesPrice(mainIncludesVat);
  const mocPrice =
    priceDisplay.mocPrice == null
      ? undefined
      : priceDisplay.mocPrice + getServicesPrice(mainIncludesVat);
  const secondaryPrice =
    priceDisplay.secondary == null
      ? undefined
      : priceDisplay.secondary + getServicesPrice(priceDisplay.secondaryIncludesVat);

  const renderErrorMessage = () => {
    if (selectedValues.length === product?.attributes?.length) {
      return (
        <span className={cn('text-2xl font-semibold text-destructive-foreground', '')}>
          {t('text495')}
        </span>
      );
    }
    if (product?.attributes?.length && selectedValues.length < product?.attributes?.length) {
      return (
        <span className={cn('text-2xl font-semibold text-destructive-foreground', '')}>
          {t('text496')}
        </span>
      );
    }
  };

  if ((!variants?.length || selectedVariant) && priceDisplay.priceOnRequest) return null;

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-1',
        'ea-product-detail-price',
      )}
    >
      {!selectedVariant && !!variants?.length ? (
        renderErrorMessage()
      ) : (
        <>
          {priceDisplay.showMocVocPrices && mocPrice != null ? (
            <div className="text-sm text-[#6B7280]">
              {`${t('text1926')} ${formatPrice(mocPrice, currency, currencySettings)}${unit}`}
            </div>
          ) : null}
          <div
            className={cn(
              'ea-product-detail-price__container',
              'flex items-baseline justify-start gap-2',
            )}
          >
            <strong
              className={cn(
                'h2',
                'font-semibold tracking-tight',
                hasProductDiscount ? 'text-[#EA2A30]' : 'text-foreground',
              )}
            >{`${formatPrice(mainPrice, currency, currencySettings)}${unit}`}</strong>
            <Paragraph size="lg" color="neutral">
              {mainIncludesVat ? t('text1122') : t('text443')}
            </Paragraph>
          </div>
          {hasProductDiscount && (
            <span className={cn('ea-product-detail-price__with-vat')}>
              <del
                className={cn('text-sm lg:text-base', 'font-normal tracking-tight text-[#6B7280]')}
              >{`${formatPrice(
                mainPriceBeforeProductDiscount,
                currency,
                currencySettings,
              )}${unit}`}</del>
            </span>
          )}
          {settings.product_detail?.show_price_without_vat?.enabled && secondaryPrice != null ? (
            <span className="text-sm text-[#6B7280]">
              {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
              {formatPrice(secondaryPrice, currency, currencySettings)} {unit}{' '}
              {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}
